import { isEmpty } from "lodash";

export { default as get } from "lodash/get";
export { default as isEmpty } from "lodash/isEmpty";
export { default as isEqual } from "lodash/isEqual";
export { default as find } from "lodash/find";
export { default as debounce } from "lodash/debounce";

export const getDateFormats = () =>
{
    const options = {
        weekday: ["short", "long", undefined],
        year: ["numeric", "2-digit"],
        month: ["2-digit", "short", "long"],
        day: ["2-digit"],
    };
    const formats = [];
    const date = new Date();
    options.weekday.forEach((weekday) =>
    {
        options.year.forEach((year) => [
            options.month.forEach((month) =>
            {
                options.day.forEach((day) =>
                {
                    let options = { weekday, year, month, day };
                    let example = new Intl.DateTimeFormat(navigator.languages[0] || navigator.language, options).format(date);
                    formats.push({ options, example });
                });
            })
        ]);
    });
    return formats;
};

/**
 *
 * @param {Date|string} date
 * @param {string} tzString
 * @returns {Date}
 */
export function convertTZ(date, tzString = Intl.DateTimeFormat().resolvedOptions().timeZone)
{
    return new Date((typeof date === "string" ? new Date(date) : date).toLocaleString(navigator.languages[0] || navigator.language, { timeZone: tzString }));
}

/**
 *
 * @param {Date} date
 * @returns {string}
 */
export function formatDate(date)
{
    return new Intl.DateTimeFormat(navigator.languages[0] || navigator.language, {
        year: "numeric",
        month: "long",
        day: "2-digit"
    }).format(date);
}

/**
 *
 * @param {string} ip
 * @returns {boolean}
 */
export function isValidIpv4(ip)
{
    if (isEmpty(ip)) return false;

    ip = ip.split(".");

    if (ip.length !== 4) return false;

    if (ip.some((num) => num === "" || (num.length > 1 && num[0] === "0"))) return false;

    ip = ip.map(Number);

    return ip.every((num) => Number.isInteger(num) && 0 <= num && num <= 255);
}

/**
 * @param {string} password
 * @returns {boolean}
 */
export function validatePassword(password)
{
    // Length at least 8 characters
    const isValidLength = password.length >= 8;

    // Upper-case
    const isUpperCase = password.search(/[A-Z]/) >= 0;

    // Lower-case
    const isLowerCase = password.search(/[a-z]/) >= 0;

    // Digit
    const isDigit = password.search(/[0-9]/) >= 0;

    // Special
    const isSpecial = password.search(/[^a-zA-Z0-9]/) >= 0;

    return { isValidLength, isUpperCase, isLowerCase, isDigit, isSpecial };
}

// Function to validate email format
export function validateEmailFormat(email) 
{
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

export function getSsoErrorMessage(trans, error) 
{
    let msg = "";
    switch (error) {
    case "invalid_input":
        msg = trans("SamlSingleSignIn.SSO_Login_With_Invalid_Email");
        break;
    case "no_access":
    case "user is blocked":
        msg = trans("SamlSingleSignIn.LOGIN_ERROR_BLOCKED_ACCOUNT");
        break;
    case "missing_saml":
        msg = trans("SamlSingleSignIn.Missing_SAML_Data");
        break;
    case "missing_user_attr":
    case "missing_details":
        msg = trans("SamlSingleSignIn.Missing_User_Attr");
        break;
    case "admins_not_allowed":
        msg = trans("SamlSingleSignIn.Admins_Not_Allowed");
        break;
    default:
        msg = error;
        break;
    }
    return msg;
}

/**
 * Sorts allItems so selected ones appear on top, based on a given key.
 * 
 * @param {Array} allItems - The full array of items (objects).
 * @param {Array} selectedItems - Array of selected item values (e.g. IDs).
 * @param {string} key - The key in each item to compare against selectedItems.
 * @param {Object|null} pinnedObj - Object with `{ key, values }` to determine which items to pin
 * @returns {Array} - Sorted array with selected items on top.
 */
export function sortItemsBySelection(allItems, selectedItems, key, pinnedObj = null) {
    // Separate pinned items and remaining items in one pass using reduce
    const [pinnedItems, remainingItems] = allItems.reduce(
        ([pinned, remaining], item) => {
            const isPinned = pinnedObj && pinnedObj.values.includes(item[pinnedObj.key]?.toString());
            (isPinned ? pinned : remaining).push(item);
            return [pinned, remaining];
        },
        [[], []] // Initial accumulator: [pinnedItems, remainingItems]
    );

    // Sort remaining items so that selected items appear on top (relative order preserved)
    const sortedItems = [...remainingItems].sort((a, b) => {
        const aSelected = selectedItems.includes(a[key]?.toString());
        const bSelected = selectedItems.includes(b[key]?.toString());

        if (aSelected && !bSelected) return -1; // 'a' comes first
        if (!aSelected && bSelected) return 1;  // 'b' comes first
        return 0; // Keep original order if both are selected or unselected
    });

    // Return the final list: pinned items first, then sorted remaining items
    return [...pinnedItems, ...sortedItems];
}
